import { getInstanceClassMeta } from "@multiconsult-group/multiplacer-core";
import type { IPixPath, IPixPolygon } from "@multiconsult-group/multiplacer-ui";

export type FloorplanIssueWithSpaces = {
  connectedBimSpaces?: {
    floor?: string | null;
    spaceDbId?: string | null;
    roomNumber?: string | null;
  }[];
};

export type FloorplanSpaceWithPolygon = {
  _id: string;
  name?: string | null;
  longName?: string | null;
  polygon?: number[][];
};

export type FloorplanSpaceWithCeilingGrids = {
  _id: string;
  ceilingGrids?: {
    lines?: number[][][];
  }[];
};

export type FloorplanMcbimItemWithGeometry = {
  _id?: unknown;
  globalId?: string;
  name?: string | null;
  longName?: string | null;
  instanceClass?: string | null;
  psets?: Record<string, Record<string, string | number | boolean>>;
  type?: {
    name?: string | null;
  } | null;
  bbox?: number[][];
  polygon?: number[][];
  geometry?: {
    polygon2d?: number[][];
    bbox2d?: number[][];
    zRelMm?: {
      bottom?: number;
      middle?: number;
      top?: number;
    };
  };
};

type SpacePolygonStyle = Pick<IPixPolygon, "fillColor" | "strokeColor" | "strokeWidth">;

const DEFAULT_SPACE_TEXT: IPixPolygon["text"] = {
  show: true,
  color: "#1f2a2a",
  fontSize: "1",
};

const HIDDEN_MCBIM_ITEM_INSTANCE_CLASSES = new Set(["IfcCovering", "IfcSlab", "IfcSpace"]);
const DEFAULT_IFC_CLASS_Z_INDEX = 20;

const hasValidPolygon = (
  space: FloorplanSpaceWithPolygon,
): space is FloorplanSpaceWithPolygon & { polygon: number[][] } =>
  Array.isArray(space.polygon) && space.polygon.length > 2;

const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.trim() !== "";

const isValidPolygonPoints = (points: unknown): points is number[][] => Array.isArray(points) && points.length > 2;

const bboxToPolygon = (bbox?: number[][]): number[][] | null => {
  if (!Array.isArray(bbox) || bbox.length < 2) return null;

  if (bbox.length >= 4) {
    const points = bbox
      .filter((point) => Array.isArray(point) && point.length >= 2)
      .map((point) => [point[0], point[1]]);
    return points.length >= 3 ? points : null;
  }

  const [minPoint, maxPoint] = bbox;
  if (!Array.isArray(minPoint) || !Array.isArray(maxPoint) || minPoint.length < 2 || maxPoint.length < 2) return null;

  const [minX, minY] = minPoint;
  const [maxX, maxY] = maxPoint;

  return [
    [minX, minY],
    [maxX, minY],
    [maxX, maxY],
    [minX, maxY],
  ];
};

export const getIssueCountByRoomNumberForFloor = (
  issues: FloorplanIssueWithSpaces[],
  selectedFloor: string,
): Map<string, number> => {
  const issueCountByRoomNumber = new Map<string, number>();
  const floorName = selectedFloor.trim();

  for (const issue of issues) {
    const linkedRoomNumbersOnFloor = new Set(
      (issue.connectedBimSpaces ?? [])
        .filter((space) => (space.floor ?? "").trim() === floorName)
        .map((space) => space.roomNumber?.trim())
        .filter(isNonEmptyString),
    );

    for (const roomNumber of linkedRoomNumbersOnFloor) {
      issueCountByRoomNumber.set(roomNumber, (issueCountByRoomNumber.get(roomNumber) ?? 0) + 1);
    }
  }

  return issueCountByRoomNumber;
};

export const isIssueConnectedToRoomOnFloor = (
  issue: FloorplanIssueWithSpaces,
  roomNumber: string,
  selectedFloor: string,
): boolean => {
  const normalizedRoomNumber = roomNumber.trim();
  const normalizedFloor = selectedFloor.trim();

  if (normalizedRoomNumber === "" || normalizedFloor === "") {
    return false;
  }

  return (issue.connectedBimSpaces ?? []).some(
    (space) =>
      (space.floor ?? "").trim() === normalizedFloor && (space.roomNumber ?? "").trim() === normalizedRoomNumber,
  );
};

export const getNormalizedRoomQuery = (roomSearchText: string): string => roomSearchText.trim().toLowerCase();

export const isSpaceSearchHit = (space: FloorplanSpaceWithPolygon, roomQuery: string): boolean =>
  roomQuery !== "" && [space.name ?? "", space.longName ?? ""].some((value) => value.toLowerCase().includes(roomQuery));

export const getSpacePolygonStyle = ({
  issueCount,
  isSelected,
  isMultiSelected,
  isSearchHit,
}: {
  issueCount: number;
  isSelected: boolean;
  isMultiSelected: boolean;
  isSearchHit: boolean;
}): SpacePolygonStyle => {
  const hasIssues = issueCount > 0;

  return {
    fillColor: isMultiSelected ? "#E9ECEF" : isSearchHit ? "#E5DBFF" : hasIssues ? "#FFF3BF" : "white",
    strokeColor: isSelected
      ? "#1C7ED6"
      : isMultiSelected
      ? "#868E96"
      : isSearchHit
      ? "#7B61FF"
      : hasIssues
      ? "#F08C00"
      : "gray",
    strokeWidth: isSelected ? "5" : isMultiSelected ? "3" : isSearchHit ? "3.5" : hasIssues ? "2" : "1.5",
  };
};

export const getSpaceTooltip = (space: FloorplanSpaceWithPolygon, issueCount: number): string | undefined => {
  const baseTooltip = [space.name, space.longName].filter(Boolean).join(" - ");

  if (issueCount > 0) {
    return `${baseTooltip}${baseTooltip ? " | " : ""}${issueCount} sak${issueCount === 1 ? "" : "er"}`;
  }

  return baseTooltip || undefined;
};

export const buildSpacePolygon = ({
  space,
  issueCount,
  selectedFloorSpaceId,
  selectedFloorSpaceIds,
  roomQuery,
}: {
  space: FloorplanSpaceWithPolygon & { polygon: number[][] };
  issueCount: number;
  selectedFloorSpaceId: string;
  selectedFloorSpaceIds: string[];
  roomQuery: string;
}): IPixPolygon => {
  const isSearchHit = isSpaceSearchHit(space, roomQuery);
  const style = getSpacePolygonStyle({
    issueCount,
    isSelected: space._id === selectedFloorSpaceId,
    isMultiSelected: selectedFloorSpaceIds.includes(space._id),
    isSearchHit,
  });

  return {
    id: space._id,
    entityId: space._id,
    points: space.polygon,
    ...style,
    text: {
      ...DEFAULT_SPACE_TEXT,
      values: [space.name ?? "", space.longName ?? ""].filter(Boolean),
    },
    tooltip: getSpaceTooltip(space, issueCount),
    isMoveable: false,
    isDragSelectable: false,
    opacity: 1,
  };
};

export const buildSpacePolygons = ({
  spaces,
  issueCountByRoomNumber,
  selectedFloorSpaceId,
  selectedFloorSpaceIds,
  roomSearchText,
}: {
  spaces: FloorplanSpaceWithPolygon[];
  issueCountByRoomNumber: Map<string, number>;
  selectedFloorSpaceId: string;
  selectedFloorSpaceIds: string[];
  roomSearchText: string;
}): IPixPolygon[] => {
  const roomQuery = getNormalizedRoomQuery(roomSearchText);

  return spaces.filter(hasValidPolygon).map((space) =>
    buildSpacePolygon({
      space,
      issueCount: issueCountByRoomNumber.get((space.name ?? "").trim()) ?? 0,
      selectedFloorSpaceId,
      selectedFloorSpaceIds,
      roomQuery,
    }),
  );
};

export const buildCeilingGridPaths = (spaces: FloorplanSpaceWithCeilingGrids[]): IPixPath[] => {
  const result: IPixPath[] = [];

  for (const space of spaces) {
    const spaceId = space._id;
    const grids = space.ceilingGrids ?? [];

    grids.forEach((grid, gridIndex) => {
      const lines = grid.lines ?? [];

      lines.forEach((line, lineIndex) => {
        result.push({
          id: `${spaceId}-cg-${gridIndex}-line-${lineIndex}`,
          points: line,
          strokeColor: "#5c5c5c",
          strokeWidth: "0.6",
        });
      });
    });
  }

  return result;
};

export const getMcbimItemPolygonPoints = (item: FloorplanMcbimItemWithGeometry): number[][] | null => {
  if (isValidPolygonPoints(item.polygon)) return item.polygon;
  if (isValidPolygonPoints(item.geometry?.polygon2d)) return item.geometry.polygon2d;

  return bboxToPolygon(item.bbox ?? item.geometry?.bbox2d);
};

export const getMcbimItemIfcClassColor = (item: FloorplanMcbimItemWithGeometry): string =>
  getInstanceClassMeta(item.instanceClass ?? "").color;

const getMcbimItemZRelMm = (item: FloorplanMcbimItemWithGeometry): number | null => {
  const zRelMm = item.geometry?.zRelMm;
  return zRelMm?.middle ?? zRelMm?.top ?? zRelMm?.bottom ?? null;
};

const getMcbimItemIfcClassZIndex = (item: FloorplanMcbimItemWithGeometry): number =>
  getInstanceClassMeta(item.instanceClass ?? "").zIndex ?? DEFAULT_IFC_CLASS_Z_INDEX;

const compareMcbimItemDrawOrder = (
  a: { item: FloorplanMcbimItemWithGeometry; itemIndex: number },
  b: { item: FloorplanMcbimItemWithGeometry; itemIndex: number },
) => {
  const aZRelMm = getMcbimItemZRelMm(a.item);
  const bZRelMm = getMcbimItemZRelMm(b.item);

  if (aZRelMm != null && bZRelMm != null && aZRelMm !== bZRelMm) {
    return aZRelMm - bZRelMm;
  }

  if (aZRelMm == null && bZRelMm != null) return -1;
  if (aZRelMm != null && bZRelMm == null) return 1;

  return getMcbimItemIfcClassZIndex(a.item) - getMcbimItemIfcClassZIndex(b.item) || a.itemIndex - b.itemIndex;
};

export const buildMcbimItemPolygon = (item: FloorplanMcbimItemWithGeometry, itemIndex: number): IPixPolygon | null => {
  if (HIDDEN_MCBIM_ITEM_INSTANCE_CLASSES.has(item.instanceClass ?? "")) {
    return null;
  }

  const points = getMcbimItemPolygonPoints(item);

  if (points == null) return null;

  const fillColor = getMcbimItemIfcClassColor(item);
  const id = String(item._id ?? item.globalId ?? `mcbim-item-${itemIndex}`);

  return {
    id: `room-item-${id}`,
    entityId: id,
    points,
    fillColor,
    strokeColor: "#4d4d4d",
    strokeWidth: "1",
    text: {
      show: false,
      values: [],
      color: "#1f2a2a",
      fontSize: "1",
    },
    tooltip: [item.instanceClass, item.name, item.longName].filter(Boolean).join(" - ") || undefined,
    isMoveable: false,
    isDragSelectable: false,
    opacity: 0.9,
  };
};

export const buildMcbimItemPolygons = (items: FloorplanMcbimItemWithGeometry[]): IPixPolygon[] =>
  items
    .map((item, itemIndex) => ({ item, itemIndex }))
    .sort(compareMcbimItemDrawOrder)
    .map(({ item, itemIndex }) => buildMcbimItemPolygon(item, itemIndex))
    .filter((polygon): polygon is IPixPolygon => polygon != null);
